import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useInterval, useLatest } from 'react-use';
import classNames from 'classnames';
import Moveable from 'react-moveable';
import { useAppSelector } from '@store/index';

import './Speaker.css';
import { getMeetingDisplayMode } from '@store/studio/studio.slice';
import { UserInfoItem } from '@tencent/wemeet-websdk-next';
import { useAttendee } from '@utils/hooks/useUserList';
import { isUserAudioOn } from '@utils/helper/media';
import { isMobileBrowser } from '@utils/browser';

/**
 *
 * 展示目前正在说话的人
 */
export default function Speaker() {
  const { t } = useTranslation();

  const meetingDisplayMode = useAppSelector(getMeetingDisplayMode);
  const userList = useAttendee();
  const latestUserList = useLatest(userList);

  const [speakerList, setSpeakerList] = useState<UserInfoItem[]>([]);

  const targetRef = useRef<HTMLDivElement>(null);
  const [frame] = useState({ translate: [0, 0] });

  const getAudioLevelSafely = (user: UserInfoItem): number => {
    try {
      return user.rtcStream?.stream?.getAudioLevel() ?? 0;
    } catch {
      return 0;
    }
  };

  const getSpeakerList = (users: UserInfoItem[]) =>
    users.filter(
      (user) =>
        // FIXME: 实际 getAudioLevel 输出的是 '0.02' 的字符串，且数值比较小，需要比较大声才能达到 0.1
        // 和 trtc 文档不符
        // https://web.sdk.qcloud.com/trtc/webrtc/doc/zh-cn/Stream.html#getAudioLevel

        // 当前音量大于 0 且用户处于开麦状态
        getAudioLevelSafely(user) > 0 && isUserAudioOn(user),
    );

  // 如果列表有值（有人在说话），那么 1s 后再更新 speakerList
  useInterval(
    () => {
      setSpeakerList(getSpeakerList(latestUserList.current));
    },
    speakerList.length ? 1000 : 100,
  );

  const speakerListString = speakerList.map((user) => user.nickname).join('; ');

  return (
    <>
      <div
        className={classNames('speaking-toast', {
          'speaking-toast--lecture': !meetingDisplayMode,
          'speaking-toast--phone': isMobileBrowser(),
        })}
        ref={targetRef}
      >
        <span className="speaking-toast__label">{t('正在说话：')}</span>
        <span className="speaking-toast__person">{speakerListString}</span>
      </div>

      <Moveable
        target={targetRef}
        snappable={true}
        draggable={true}
        origin={true}
        bounds={{ left: 1, top: 1, right: document.body.clientWidth, bottom: document.body.clientHeight }}
        padding={{ left: 0, top: 0, right: 0, bottom: 0 }}
        onDragStart={(e) => {
          e.set(frame.translate);
        }}
        onDrag={(e) => {
          frame.translate = e.beforeTranslate;
          e.target.style.transform = `translate(${e.beforeTranslate[0]}px, ${e.beforeTranslate[1]}px)`;
        }}
      />
    </>
  );
}
